import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import { Tenancy } from './DataModels';
import { ChangeEvent, JSX } from 'react';
import { IRouterProps, Typography, withRouting } from "@iconics/components";
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextField } from "@iconics/components";
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { SerendieSymbol } from '@serendie/symbols';
import { Switch } from '@serendie/ui';
import { Validator } from '@iconics/core';

class EditTenancy extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private languageValuesHelper: LanguageValuesHelper;

    private get tenancyId() { return this.props.params.id ?? '' }
    private get isNew() { return this.props.params.id == undefined }

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
        const localStorageProvider = appContext().localStorageProvider;

        this.state =
        {
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            enabled: false,
            concurrencyToken: '',
            canDelete: localStorageProvider.hasRight('DATAMODEL.Tenancy.Delete'),
            sections: [],
            adminEmail1: '',
            adminEmail1Error: '',
            adminEmail2: '',
            adminEmail2Error: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Tenancy_PageTitle;
        this.backUrl = '/admin/tenancies';

        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired({ errorLabel: this.labels.Platform_Tenancy_Required })

        if (this.isNew)
        {
            this.validator
                .addInput('adminEmail1', () => this.state.adminEmail1, i => this.setState({ adminEmail1Error: i }))
                .isRequired()
                .isValidEmail();

            this.validator
                .addInput('adminEmail2', () => this.state.adminEmail2, i => this.setState({ adminEmail2Error: i }))
                .isEqualTo(() => this.state.adminEmail1, { errorLabel: appContext().labels.Platform_Tenancy_AdminEmail2_MustEqualAdminEmail1 });
        }

        await this.loadTenancy();

    }

    public async componentDidUpdate(prevProps: Readonly<IProps>, _: Readonly<IState>): Promise<void>
    {
        if (!prevProps.params.id && this.props.params.id)
        {
            await this.loadTenancy();
        }
    }

    private async loadTenancy(): Promise<void>
    {
        await this.loading(async () =>
        {
            if (this.props.params.id === undefined)
            {
                return;
            }
            const tenancyData = await appContext().fadxPlatformApiClientV2.v2.core.tenancies.byId.get<Tenancy>({
                id: this.props.params.id,
                select: Tenancy,
            });

            await this.setStateAsync({
                name: tenancyData.Name,
                enabled: tenancyData.IsEnabled === 'Yes',
                concurrencyToken: tenancyData["@odata.etag"],
                displayName: this.languageValuesHelper.fromString(tenancyData.DisplayName),
            });
        });
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.tenancies.byId.delete({ id: this.tenancyId });
            this.props.navigate('/admin/tenancies');
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (this.props.params.id)
            {
                await this.updateTenancy();
            }
            else
            {
                await this.createTenancy();
            }
            this.props.navigate('/admin/tenancies');
        });
    }

    private async updateTenancy(): Promise<void>
    {
        await appContext().fadxPlatformApiClientV2.v2.core.tenancies.byId.patch<Tenancy>({
            id: this.tenancyId,
            body: {
                Name: this.state.name,
                IsEnabled: this.state.enabled ? 'Yes' : 'No',
                Root_NodeId: '1',
                DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                '@odata.etag': this.state.concurrencyToken,
            }
        });
    }

    private async createTenancy(): Promise<void>
    {
        await appContext().fadxPlatformApiClientV2.v2.core.tenancies.post<Tenancy>({
            body: {
                Name: this.state.name,
                IsEnabled: this.state.enabled ? 'Yes' : 'No',
                DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                TenancyAdminEmail: this.state.adminEmail1,
                PreferredName: this.state.adminEmail1,
                Initials: '',
            }
        });
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer>
                        <GridItem>
                            <Card
                                title={labels.Platform_Tenancy_DetailsTitle}
                                subTitle={labels.Platform_Tenancy_DetailsSubTitle}
                                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                                icon={<SerendieSymbol name='layout-left' />}
                            >
                                <GridContainer>
                                    <GridItem expanded={6}>
                                        <TextField
                                            required
                                            label={labels.Tenancy_Name}
                                            placeholder={labels.Tenancy_Name}
                                            value={this.state.name}
                                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                            invalid={!!this.state.nameError}
                                            invalidMessage={this.state.nameError}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <MultilingualTextField
                                            onChange={value => this.setState({ displayName: value })}
                                            label={labels.Tenancy_DisplayName}
                                            placeholder={labels.Tenancy_DisplayName}
                                            value={this.state.displayName}
                                            localisation={appContext().localisation}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Switch
                                            label={labels.Tenancy_IsEnabled}
                                            checked={this.state.enabled}
                                            onCheckedChange={e => this.setState({ enabled: e.checked as boolean })}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </Card>
                        </GridItem>
                        {
                            this.isNew &&
                            <GridItem>
                                <Card
                                    title={labels.Platform_Tenancy_UserDetailsTitle}
                                    subTitle={labels.Platform_Tenancy_UserDetailsSubTitle}
                                    icon={<SerendieSymbol name='user' />}
                                >
                                    <GridContainer>
                                        <GridItem expanded={6}>
                                            <TextField
                                                required
                                                label={labels.Platform_Tenancy_AdminEmail_1}
                                                placeholder={labels.Platform_Tenancy_AdminEmail_1}
                                                value={this.state.adminEmail1}
                                                onChange={e => this.setState({ adminEmail1: e.target.value }, () => { this.validator.validate('adminEmail1'); this.validator.validate('adminEmail2'); })}
                                                invalid={!!this.state.adminEmail1Error}
                                                invalidMessage={this.state.adminEmail1Error}
                                            />
                                        </GridItem>
                                        <GridItem expanded={6}>
                                            <TextField
                                                required
                                                label={labels.Platform_Tenancy_AdminEmail_2}
                                                placeholder={labels.Platform_Tenancy_AdminEmail_2}
                                                value={this.state.adminEmail2}
                                                onChange={e => this.setState({ adminEmail2: e.target.value }, () => this.validator.validate('adminEmail2'))}
                                                invalid={!!this.state.adminEmail2Error}
                                                invalidMessage={this.state.adminEmail2Error}
                                            />
                                        </GridItem>
                                        <GridItem expanded={12}>
                                            <Typography role='body'>
                                                {labels.Platform_Tenancy_AdminEmail_Advice}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </Card>
                            </GridItem>
                        }
                    </GridContainer>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !this.isNew &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    dialogMessage={this.labels.Platform_Tenancy_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate('/admin/tenancies')}
                            >
                                {labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.validator.isValid}
                            >
                                {labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        );
    }


}

export default withRouting(EditTenancy);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id?: string;
}

export interface IState
{
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    enabled: boolean;
    concurrencyToken: string;
    canDelete: boolean;
    sections: IPageNavSection[];
    adminEmail1: string;
    adminEmail1Error: string;
    adminEmail2: string;
    adminEmail2Error: string;
}
